import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const HomepageBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/blog/homepage/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching homepage posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Improved image URL helper that properly handles paths
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // Remove /public prefix if present (this is the key fix)
    let cleanPath = imageUrl;
    if (cleanPath.startsWith('/public/')) {
      cleanPath = cleanPath.replace('/public', '');
    }
    
    // Ensure path starts with /
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    
    return `https://graduates.katinetti.ac.ke${cleanPath}`;
  };

  const truncateText = (text, length = 100) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  // Auto-scroll functionality for mobile
  useEffect(() => {
    if (!scrollContainerRef.current || posts.length <= 2) return;

    let scrollInterval;
    const container = scrollContainerRef.current;

    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (container && window.innerWidth < 768) {
          const maxScroll = container.scrollWidth - container.clientWidth;
          if (container.scrollLeft >= maxScroll - 10) {
            container.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            container.scrollBy({ left: 320, behavior: 'smooth' });
          }
          setTimeout(checkScrollPosition, 500);
        }
      }, 5000);
    };

    const stopAutoScroll = () => {
      if (scrollInterval) clearInterval(scrollInterval);
    };

    startAutoScroll();

    container.addEventListener('mouseenter', stopAutoScroll);
    container.addEventListener('touchstart', stopAutoScroll);
    container.addEventListener('mouseleave', startAutoScroll);
    container.addEventListener('touchend', startAutoScroll);
    container.addEventListener('scroll', checkScrollPosition);

    checkScrollPosition();

    return () => {
      stopAutoScroll();
      if (container) {
        container.removeEventListener('mouseenter', stopAutoScroll);
        container.removeEventListener('touchstart', stopAutoScroll);
        container.removeEventListener('mouseleave', startAutoScroll);
        container.removeEventListener('touchend', startAutoScroll);
        container.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [posts]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkScrollPosition, 500);
    }
  };

  if (loading) return null;
  if (posts.length === 0) return null;

  const latestPost = posts[0];
  const otherPosts = posts.slice(1);

  return (
    <section className="relative py-12 overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/images/blog-bg.jpg)',
          backgroundPosition: 'center center',
          backgroundSize: 'cover'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/80 to-secondary/85"></div>

      <div className="relative container mx-auto px-4 z-10">
        {/* Left aligned header */}
        <div className="mb-8">
          <span className="inline-block px-3 py-1 bg-[#fbbf24]/20 text-[#fbbf24] rounded-full text-xs font-semibold mb-3">
            Stay Updated
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Latest News & Updates
          </h2>
          <p className="text-gray-200 text-sm md:text-base max-w-2xl">
            Stay informed with the latest happenings at Katine TTI
          </p>
          <div className="w-16 h-0.5 bg-[#fbbf24] mt-3 rounded-full"></div>
        </div>

        {/* Desktop Layout: 3 columns */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.slice(0, 3).map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group transform transition-all duration-300 hover:-translate-y-1"
            >
              <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                <div className="relative h-44 overflow-hidden">
                  {getImageUrl(post.featured_image) ? (
                    <img
                      src={getImageUrl(post.featured_image)}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        console.error('Image failed to load:', getImageUrl(post.featured_image));
                        e.target.onerror = null;
                        e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><span class="text-white text-4xl">📰</span></div>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <span className="text-white text-4xl">📰</span>
                    </div>
                  )}
                  {post.category_name && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 bg-[#fbbf24] text-gray-900 text-xs font-semibold rounded-full">
                      {post.category_name}
                    </span>
                  )}
                  <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 rounded-md backdrop-blur-sm">
                    <span className="text-white text-xs">
                      {new Date(post.published_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 text-base mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-xs line-clamp-2">
                    {post.excerpt || truncateText(post.content, 80)}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[#fbbf24] text-xs font-medium group-hover:underline flex items-center gap-1">
                      Read More
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {post.view_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile Layout: Featured post + scrollable cards */}
        <div className="md:hidden">
          {/* Featured Latest Post - Full width */}
          {latestPost && (
            <Link
              to={`/blog/${latestPost.slug}`}
              className="block mb-6 group"
            >
              <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative h-56 overflow-hidden">
                  {getImageUrl(latestPost.featured_image) ? (
                    <img
                      src={getImageUrl(latestPost.featured_image)}
                      alt={latestPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><span class="text-white text-5xl">📰</span></div>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <span className="text-white text-5xl">📰</span>
                    </div>
                  )}
                  {latestPost.category_name && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 bg-[#fbbf24] text-gray-900 text-xs font-semibold rounded-full">
                      {latestPost.category_name}
                    </span>
                  )}
                  <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 rounded-md backdrop-blur-sm">
                    <span className="text-white text-xs">
                      {new Date(latestPost.published_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {latestPost.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3">
                    {latestPost.excerpt || truncateText(latestPost.content, 120)}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[#fbbf24] text-sm font-medium group-hover:underline flex items-center gap-1">
                      Read More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {latestPost.view_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Scrollable Other Posts */}
          {otherPosts.length > 0 && (
            <div className="relative">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-white text-sm font-semibold">More News</h3>
              </div>

              {/* Centered Arrow Buttons */}
              <div className="relative">
                {showLeftArrow && (
                  <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#fbbf24] text-gray-900 flex items-center justify-center hover:bg-[#e6a812] shadow-lg transition-all duration-300"
                    style={{ transform: 'translateY(-50%)' }}
                  >
                    ←
                  </button>
                )}

                <div
                  ref={scrollContainerRef}
                  className="flex overflow-x-auto gap-4 pb-4 scroll-smooth"
                  style={{
                    scrollbarWidth: 'thin',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarColor: '#fbbf24 #f0f0f0'
                  }}
                >
                  {otherPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="flex-shrink-0 w-[calc(50%-8px)] min-w-[160px] group transform transition-all duration-300 hover:-translate-y-1"
                      style={{ width: 'calc(50% - 8px)' }}
                    >
                      <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                        <div className="relative h-32 overflow-hidden">
                          {getImageUrl(post.featured_image) ? (
                            <img
                              src={getImageUrl(post.featured_image)}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><span class="text-white text-2xl">📰</span></div>';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                              <span className="text-white text-2xl">📰</span>
                            </div>
                          )}
                          {post.category_name && (
                            <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#fbbf24] text-gray-900 text-[10px] font-semibold rounded-full">
                              {post.category_name}
                            </span>
                          )}
                          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/50 rounded-md backdrop-blur-sm">
                            <span className="text-white text-[9px]">
                              {new Date(post.published_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="p-2">
                          <h3 className="font-bold text-gray-800 text-xs mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-[10px] line-clamp-2">
                            {post.excerpt || truncateText(post.content, 50)}
                          </p>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-[#fbbf24] text-[10px] font-medium group-hover:underline flex items-center gap-0.5">
                              Read
                              <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                              <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {post.view_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {showRightArrow && (
                  <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#fbbf24] text-gray-900 flex items-center justify-center hover:bg-[#e6a812] shadow-lg transition-all duration-300"
                    style={{ transform: 'translateY(-50%)' }}
                  >
                    →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#fbbf24] text-gray-900 rounded-lg hover:bg-[#e6a812] transition-all duration-300 font-medium shadow-md hover:shadow-lg text-sm"
          >
            View All News
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomepageBlog;
